import Head from 'next/head';
import type { FC } from 'react';
import { memo } from 'react';
import type {
  DiscussionForumPosting,
  NewsArticle,
  Organization,
  Person,
  WithContext,
} from 'schema-dts';

import { apiUrl } from '../../config';
import { useWebsite, useWebsiteMeta } from '../../hooks/useWebsite';
import type { Category, Guest, Message, Topic, User, Vote } from '../../types/api';
import { sliceUnicodeAware } from '../../utils/common-utils';
import { stripHtml } from '../../utils/dom';
import { computeImageSource } from '../../utils/string';
import { absoluteUrl, profileUrl } from '../../utils/url';
import { buildApiFileName } from '../../utils/web';
import { getTopicLastModifiedStr } from '../thread-utils';
// `messagesToComments` extrait pour être partagé hors composant (B8_01/C13) : le mapping
// `messages → Comment[]` (ex-`ThreadMessagesLd`) alimente désormais le `comment[]` du DFP unique.
import { messagesToComments } from '../ThreadMessage/messagesToComments';

const completeTemplate = ({ template, data }: { template: string; data: any }) => {
  if (!template) return '';
  return template.replace(/{([^{]*)}/g, match => data[match.slice(1, -1)] || '');
};

interface Props {
  topic: Topic;
  /** Premières réponses du thread, rendues en SSR (B8_01) — fusionnées en `comment[]` dans le DFP forum
   * unique (D33/C13), remplace le 2ᵈ DFP client-only supprimé. Absent en mode presse (NewsArticle). */
  messages?: Message[];
}

export const TopicLd: FC<Props> = memo(function TopicLd(props) {
  // if (isBrowser && !frontConfig.enableSEOLdInFront) {
  //   return null;
  // }
  return <TopicLdInner {...props} />;
});

export const TopicLdInner: FC<Props> = memo(function TopicLdInner(props) {
  const { topic, messages } = props;
  const {
    name: websiteName = '',
    url: websiteUrl = '',
    fullBasepathUrl = '',
    custom = {},
    graphdebate,
  } = useWebsite();
  const { topic: topicMeta = {} } = useWebsiteMeta('topic');
  // Article de presse : mode média du tenant + `format` renseigné (même discriminateur que MediaArticle
  // et [slug].tsx). Émet un `NewsArticle` au lieu du `DiscussionForumPosting` forum (B6_02).
  const isMediaArticle = graphdebate?.mode === 'media' && !!topic.format;
  const { subject: rawSubject, content: rawContent, votes: rawVotes = [], messagesCount } = topic;
  const votes = rawVotes as Vote[];
  const { image } = custom;
  // Once I've found a category which is "null". Maybe an old, removed category?
  const categories = (topic?.categories?.filter(c => c) || []) as Category[];

  const subject = stripHtml(rawSubject as string) || '';
  const content = stripHtml(rawContent as string) || '';
  const topicExtractedData = {
    pageSubject: subject,
    pageContent: content,
    pageDate: topic?.createdAt,
    firstTagTitle: categories[0]?.title ?? '',
    websiteName,
    websiteUrl,
  };
  const completedTitle = completeTemplate({
    template: topicMeta?.title ?? (subject || ''),
    data: topicExtractedData,
  });
  const completedDescription = completeTemplate({
    template: topicMeta?.description ?? (content || ''),
    data: topicExtractedData,
  });
  // URL de page tenant : composée depuis `fullBasepathUrl` (porte le basePath d'un tenant en
  // sous-répertoire, ex. `.../forums`), pas depuis `website.url` nu — sinon le LD contredit la canonical
  // sur un tenant à basePath (B8_02). `absoluteUrl` réduit un `//` accidentel sans manger le domaine (B6_03).
  const url = absoluteUrl(fullBasepathUrl, `/t/${topic.slug}`);
  // Logo du tenant : `custom.image` est un chemin servi par le CDN images (host `apiUrl`), PAS par le
  // domaine du tenant → jamais préfixé par le basePath (idiome `MetaWebsite.tsx` og:image). Le fallback
  // apple-touch-icon est servi depuis le domaine tenant (avec basePath).
  const logoUrl = image
    ? image.startsWith('/')
      ? absoluteUrl(apiUrl, image)
      : image
    : absoluteUrl(fullBasepathUrl, '/images/apple-touch-icon.png');

  const authorPicture =
    (topic?.author as User)?.profile?.picture ?? (topic?.author as Guest)?.picture;
  const userProfile = (topic?.author as User)?.profile;
  const pseudo = userProfile?.pseudo;
  const authorName = pseudo ?? topic?.author?.username ?? `Anonymous#${topic.id}`;

  const authorPersonEntity: Person = {
    '@type': 'Person',
    name: authorName,
    // Avatar en URL absolue : `authorPicture` peut être un chemin relatif (`/images/avatar_3.png`),
    // invalide dans un LD → `computeImageSource` préfixe le CDN images (host `imageUrl`) si relatif,
    // laisse une URL absolue intacte (B8_03).
    image: computeImageSource(authorPicture),
    // URL composée par `profileUrl` (pseudo percent-encodé, source unique B6_12 — ex-B6_11).
    ...(!!pseudo && { url: profileUrl(fullBasepathUrl, pseudo) }),
  };

  const organizationEntity: Organization = {
    '@type': 'Organization',
    name: websiteName,
    // Le tenant EST son forum : sur un tenant à basePath, l'Organization vit à `.../forums`, pas à la
    // racine du domaine hôte (404 Reworld). → `fullBasepathUrl` (décision fenêtre B8_02).
    url: fullBasepathUrl,
  };

  // Switch author if pseudo is not set to prevent GoogleSearchConsole error
  const author = userProfile ? authorPersonEntity : organizationEntity;

  // Score net des votes du thread (upvotes − downvotes, borné ≥ 0) — exposé en LikeAction du DFP (B8_03).
  const upVoteCount = Math.max(
    votes.filter(elt => Number(elt.score ?? 0) > 0).length -
      votes.filter(elt => Number(elt.score ?? 0) < 0).length,
    0,
  );

  const lastModified = getTopicLastModifiedStr(topic);
  const dateData = {
    dateCreated: topic?.createdAt,
    datePublished: topic?.createdAt,
    dateModified: lastModified,
  };

  const answerCount = topic?.findOrCreateThread?.countMessages || topic.messagesCount;

  const discussionObject: WithContext<DiscussionForumPosting> = {
    '@context': 'https://schema.org',
    '@type': 'DiscussionForumPosting',
    image: logoUrl,
    url,
    mainEntityOfPage: {
      '@type': 'WebPage',
      '@id': url,
    },
    // Sujet NU (sans le suffixe ` - {websiteName}` du template datalayer), tronqué à 110 (recommandation
    // Google pour un headline) — l'ancien 80 partait de `completedTitle` (titre + suffixe site) (B8_03).
    headline: sliceUnicodeAware(subject, 0, 110),
    text: completedDescription,
    description: completedDescription,
    keywords: categories.map(tag => tag.title || tag.slug).join(','),
    ...dateData,
    publisher: {
      '@type': 'Organization',
      legalName: websiteName ?? '',
      name: websiteName ?? '',
      logo: {
        '@type': 'ImageObject',
        contentUrl: logoUrl,
        url: logoUrl,
      },
    },
    author,
    // Deux interactions : nombre de réponses (CommentAction) + votes nets du thread (LikeAction) (B8_03).
    interactionStatistic: [
      {
        '@type': 'InteractionCounter',
        interactionType: { '@type': 'CommentAction' },
        userInteractionCount: answerCount,
      },
      {
        '@type': 'InteractionCounter',
        interactionType: { '@type': 'LikeAction' },
        userInteractionCount: upVoteCount,
      },
    ],
    about: categories.map(({ title, slug }) => ({ '@type': 'DefinedTerm', name: title || slug })),
    ...(messagesCount && { commentCount: messagesCount }),
    // `comment[]` des premières réponses rendues en SSR (B8_01/C13) : author/date/text par réponse,
    // fusionné dans le DFP unique (D33) — `comment.length ≤ commentCount` (sous-ensemble 1ʳᵉ page).
    ...(messages?.length
      ? {
          comment: messagesToComments(messages, {
            fullBasepathUrl,
            topicSlug: topic.slug as string,
          }),
        }
      : {}),
  };

  // Image de une réelle de l'article (hero) pour le `NewsArticle` — URL absolue ; à défaut, le logo.
  const hero = topic.hero;
  const heroImageUrl =
    hero?.imageUrl || (hero?.image?.id ? buildApiFileName(hero.image.id) : '') || logoUrl;
  const wordCount = content ? content.trim().split(/\s+/).filter(Boolean).length : undefined;

  const newsArticleObject: WithContext<NewsArticle> = {
    '@context': 'https://schema.org',
    '@type': 'NewsArticle',
    url,
    mainEntityOfPage: {
      '@type': 'WebPage',
      '@id': url,
    },
    // headline complet (le forum le tronquait à 80 ; Google recommande ≤ 110 pour un article).
    headline: sliceUnicodeAware(completedTitle, 0, 110),
    description: completedDescription,
    image: heroImageUrl,
    ...dateData,
    author,
    publisher: {
      '@type': 'Organization',
      legalName: websiteName ?? '',
      name: websiteName ?? '',
      logo: {
        '@type': 'ImageObject',
        contentUrl: logoUrl,
        url: logoUrl,
      },
    },
    articleSection: categories[0]?.title || categories[0]?.slug,
    keywords: categories.map(tag => tag.title || tag.slug).join(','),
    // Tenant média mono-langue (français). À dériver de la locale du tenant si le média devient multilingue.
    inLanguage: 'fr',
    ...(wordCount ? { wordCount } : {}),
  };

  const jsonld = isMediaArticle ? newsArticleObject : discussionObject;

  return (
    <Head>
      <script
        key={`json-lds-topic-${topic?.id}`}
        type="application/ld+json"
        // JSON-LD : JSON.stringify échappe le contenu ; on encode `<` en `\u003c` pour empêcher toute
        // évasion `</script>` si un champ (sujet, contenu, nom du tenant) portait du HTML (idiome
        // PersonLd, généralisé B6_12).
        dangerouslySetInnerHTML={{ __html: JSON.stringify(jsonld).replace(/</g, '\\u003c') }}
      />
    </Head>
  );
});
